import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import classnames from 'classnames';
import {defer, isUndefined} from 'lodash';
import {callTargetedAction} from 'client/framework';
import type {QuestionModelV3} from 'resources/augmented/Question/QuestionModel.v3';

import textHighlightQuestionActions from '../TextHighlightQuestion.actions';

import {useTranslatedHighlightPrompt} from './useTranslatedHighlightPrompt';

interface TextHighlightPromptProps {
  explanation?: boolean;
  isGuessCorrect?: boolean;
  isGuessSubmitted?: boolean;
  question?: QuestionModelV3;
  storeName?: string;
  className?: string;
  showTranslation?: boolean;
  activeQuestionStore?: any;
}

const getCharIndex = (target: EventTarget | null) =>
  target instanceof Element
    ? target.closest<HTMLElement>('.text-highlight-area__char[data-index]')?.dataset.index
    : undefined;

/*
legacy class propTypes ref
  static propTypes = {
    explanation: PropTypes.bool,
    isGuessCorrect: PropTypes.bool,
    isGuessSubmitted: PropTypes.bool,
    question: PropTypes.instanceOf(QuestionModelV3),
    storeName: PropTypes.string,
    className: PropTypes.string
  };
*/

const TextHighlightPrompt = ({
  explanation,
  isGuessCorrect,
  isGuessSubmitted,
  question,
  storeName,
  className,
  showTranslation = false,
  activeQuestionStore
}: TextHighlightPromptProps) => {
  const store = activeQuestionStore ?? question?.getStore?.();

  // Unmemoized: the store is mutable and fills in after mount, so it can't key a memo.
  const highlightPrompt = useTranslatedHighlightPrompt() ?? store.highlightPrompt;

  // wrapper ref ---------
  const highlightAreaWrapperNode = useRef<HTMLDivElement>(null);
  const mouseupListenerFunc = useRef<((e: MouseEvent) => void) | null>(null);
  const touchListenerFunc = useRef<((e: TouchEvent) => void) | null>(null);
  const mouseMoveTicking = useRef<boolean>(false);
  // const [mouseMoveTicking, setMouseMoveTicking] = useState(false);

  useEffect(() => {
    return () => {
      if (mouseupListenerFunc.current) {
        // handleEndSelection
        global.document.removeEventListener('mouseup', mouseupListenerFunc.current);
      }
      if (touchListenerFunc.current) {
        // handleTouchEnd
        global.document.removeEventListener('touchend', touchListenerFunc.current);
      }
    };
  }, []);

  // prompt logic and rendering

  const getClassNames = useCallback(
    (index: number) => {
      const baseClass = 'text-highlight-area__char';
      const highlightClass = 'text-highlight-area__char--highlight';

      if (explanation) {
        return classnames(baseClass, {
          [highlightClass]: store.isIndexInPreferredPermutation(index)
        });
      }

      return classnames(baseClass, {
        [highlightClass]: store.isIndexSelected(index),
        'text-highlight-area__char--current': store.isIndexInCurrentSelection(index),
        'text-highlight-area__char--limit':
          index === store.startIndex || index === store.potentialIndex
      });
    },
    [store, explanation]
  );

  const getTargetElementFromTouchEventData = useCallback(
    (eventProperty: TouchList | React.TouchList) => {
      const {pageX, pageY} = eventProperty[eventProperty.length - 1];
      const {scrollX, scrollY} = global.window;
      const xPos = pageX - scrollX;
      const yPos = pageY - scrollY;
      return global.document.elementFromPoint(xPos, yPos);
    },
    []
  );

  const pendingSelectionEnd = useCallback(
    (index: string | undefined) => {
      if (store.startIndex === null) {
        return;
      }
      callTargetedAction({
        name: textHighlightQuestionActions.POTENTIAL_SELECTION,
        payload: index,
        targetStore: storeName
      });
    },
    [store, storeName]
  );

  // main selection functions
  const startSelection = useCallback(
    (index: string | undefined) => {
      if (store.isGuessSubmitted) {
        return;
      }
      callTargetedAction({
        name: textHighlightQuestionActions.START_SELECTION,
        payload: index,
        targetStore: storeName
      });
    },
    [storeName, store]
  );

  const endSelection = useCallback(
    (index: string | undefined, targetEl) => {
      if (isUndefined(index) || !highlightAreaWrapperNode.current?.contains(targetEl)) {
        callTargetedAction({
          name: textHighlightQuestionActions.CLEAR_PENDING_SELECTION,
          targetStore: storeName
        });
        return;
      }
      callTargetedAction({
        name: textHighlightQuestionActions.END_SELECTION,
        payload: index,
        targetStore: storeName
      });
    },
    [storeName]
  );

  // handlers

  const handleEndSelection = useCallback(
    (e: MouseEvent) => {
      global.document.removeEventListener('mouseup', mouseupListenerFunc.current!);
      endSelection(getCharIndex(e.target), e.target);
      mouseupListenerFunc.current = null;
    },
    [endSelection]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      global.document.addEventListener('mouseup', handleEndSelection);
      mouseupListenerFunc.current = handleEndSelection;
      startSelection(getCharIndex(e.target));
    },
    [startSelection, handleEndSelection]
  );

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      global.document.removeEventListener('touchend', touchListenerFunc.current!);
      touchListenerFunc.current = null;
      const target = getTargetElementFromTouchEventData(e.changedTouches);
      endSelection(getCharIndex(target), target);
    },
    [endSelection, getTargetElementFromTouchEventData]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLSpanElement>) => {
      e.preventDefault();
      global.document.addEventListener('touchend', handleTouchEnd);
      touchListenerFunc.current = handleTouchEnd;
      startSelection(getCharIndex(e.target));
    },
    [startSelection, handleTouchEnd]
  );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      pendingSelectionEnd(getCharIndex(e.target));
    },
    [pendingSelectionEnd]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLSpanElement>) => {
      e.preventDefault();
      if (mouseMoveTicking.current) {
        return;
      }
      mouseMoveTicking.current = true;
      pendingSelectionEnd(getCharIndex(getTargetElementFromTouchEventData(e.targetTouches)));
      defer(() => {
        mouseMoveTicking.current = false;
      });
    },
    [mouseMoveTicking, pendingSelectionEnd, getTargetElementFromTouchEventData]
  );

  // Children react components

  // logic booleans -------
  const isExplanation = useMemo(() => !!explanation, [explanation]);
  const shouldPreventInteraction = useMemo(
    () => isGuessSubmitted || isExplanation,
    [isGuessSubmitted, isExplanation]
  );
  // props.isGuessCorrect is false if you haven't answered, which is
  // why we're being so explicit about this
  const isCorrect = useMemo(
    () => isGuessSubmitted && isGuessCorrect,
    [isGuessCorrect, isGuessSubmitted]
  );
  const isIncorrect = useMemo(
    () => isGuessSubmitted && !isGuessCorrect,
    [isGuessSubmitted, isGuessCorrect]
  );

  // styling ------------
  const classNames = useMemo(
    () =>
      classnames('text-highlight-area', className, {
        'text-highlight-area--prevent-interaction': shouldPreventInteraction || showTranslation,
        'text-highlight-area--correct': isCorrect,
        'text-highlight-area--incorrect': isIncorrect && !isExplanation
      }),
    [isCorrect, isIncorrect, shouldPreventInteraction, isExplanation, showTranslation, className]
  );

  return (
    <div className={classNames} ref={highlightAreaWrapperNode}>
      {highlightPrompt.split('').map((char: string, i: number) => {
        return (
          <span
            // oxlint-disable-next-line react/no-array-index-key -- per-character positional rendering; index is the character position
            key={i}
            className={getClassNames(i)}
            data-index={`${i}`}
            onMouseDown={isGuessSubmitted ? undefined : handleMouseDown}
            onMouseEnter={isGuessSubmitted ? undefined : handleMouseEnter}
            onTouchStart={isGuessSubmitted ? undefined : handleTouchStart}
            onTouchMove={isGuessSubmitted ? undefined : handleTouchMove}
          >
            {char}
          </span>
        );
      })}
    </div>
  );
};

export default TextHighlightPrompt;
